import React from 'react';

import { DownloadIcon } from '@reckon-web/icon';
import { Flex } from '@reckon-web/flex';
import { IconButton } from '@reckon-web/button';
import { Tooltip } from '@reckon-web/tooltip';
import { makeId } from '@reckon-web/utils';
import { FilterIcon } from '@reckon-web/icon';
import { ConfigMenu } from '@reckon-web/config-menu';
import { Text } from '@reckon-web/text';

import { GetUserFilterListResult } from '../../data/useGetUserFilterList';

import { VisibleFilters } from './visible-filters';
import { VisibleProperties, WorkType } from './visible-properties';
import { Sort, SortType } from './sort';
import { JobsOrTasks } from './jobs-or-tasks';
import { KanbanOrList, KanbanOrListToggle } from './kanban-or-list';

export const DataControls: React.FC<{
  jobsOrTasks: JobsOrTasks;
  workType?: WorkType;
  kanbanOrList: KanbanOrList;
  showFilterIcon?: boolean;
  setKanbanOrList: (kanbanOrList: KanbanOrList) => void;
  sort: SortType;
  setSort: (sort: SortType) => void;
  exportToCsv: () => void;
  userFilters?: GetUserFilterListResult[];
  applyUserFilter?: (userFilter: GetUserFilterListResult) => void;
  canExport?: boolean;
  testId?: string;
}> = ({
  jobsOrTasks,
  workType,
  kanbanOrList,
  showFilterIcon,
  setKanbanOrList,
  sort,
  setSort,
  exportToCsv,
  userFilters,
  applyUserFilter,
  canExport,
  testId = `data-controls`,
}) => {
  return (
    <Flex gap="small" data={{ testid: testId }}>
      {kanbanOrList === 'kanban' && (
        <Sort sort={sort} setSort={setSort} testId={makeId(testId, 'sort')} />
      )}
      <Flex gap="xsmall">
        {!!userFilters && !!userFilters.length && (
          <VisibleFilters
            userFilters={userFilters}
            setKanbanOrList={setKanbanOrList}
            applyUserFilter={applyUserFilter}
            data-testid={makeId(testId, 'filters')}
          />
        )}
        {/*NOTE: this is a hacky approach as the filter will be replaced with
           "My views" (or another similar concept) in the near future, we can leave this as a placeholder. */}
        {showFilterIcon && !userFilters?.length && (
          <ConfigMenu
            data-testid={testId}
            trigger={({ triggerProps }) => (
              <Tooltip content="Saved filters and more filters">
                <IconButton
                  label="Saved filters and more filters"
                  icon={FilterIcon}
                  size="small"
                  weight="none"
                  tone="passive"
                  data-testid={makeId(testId, 'saved-filters')}
                  {...triggerProps}
                />
              </Tooltip>
            )}
          >
            <Flex
              direction="column"
              gap="medium"
              paddingX="medium"
              paddingY="small"
              data={{ testid: makeId(testId, 'user-filters') }}
            >
              <Text size="xsmall" color="muted">
                Saved filters
              </Text>
              <Text size="small" weight="medium" color="dim">
                No filters saved yet
              </Text>
            </Flex>
          </ConfigMenu>
        )}

        {/* If the user didn't pass in any workType, then use jobsOrTask */}
        <VisibleProperties
          workType={workType || jobsOrTasks}
          kanbanOrList={kanbanOrList}
          data-testid={makeId(testId, 'properties')}
        />
        <Tooltip content={canExport ? 'Export to .CSV' : 'Nothing to export'}>
          <IconButton
            label="Download"
            icon={DownloadIcon}
            size="small"
            weight="none"
            tone="passive"
            onClick={exportToCsv}
            disabled={!canExport}
            data-testid={makeId(testId, 'download')}
          />
        </Tooltip>
      </Flex>
      <KanbanOrListToggle
        kanbanOrList={kanbanOrList}
        setKanbanOrList={setKanbanOrList}
        data-testid={makeId(testId, 'kanban-or-list-toggle')}
      />
    </Flex>
  );
};
